import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { NAV_LINKS, PHONE, PHONE_HREF } from '../data/content';

const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNavigate(href);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled || open ? 'bg-ink/95 backdrop-blur-xl border-b border-white/5' : 'bg-black/20 backdrop-blur-md border-b border-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
        <button data-testid="nav-logo" onClick={() => go('#home')} className="flex items-center gap-3 text-left">
          <img src="/images/alas-logo.png" alt="ALAS Velocity logo" className="h-10 w-10 rounded-full" />
          <span>
            <span className="font-display text-xl tracking-[0.18em] text-white">ALAS</span>
            <span className="ml-2 text-xs font-medium uppercase tracking-[0.4em] text-gold">Velocity</span>
          </span>
        </button>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => go(l.href)}
              className="nav-link whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.14em] text-white/80 transition-colors duration-300 hover:text-gold"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <a data-testid="nav-phone-link" href={PHONE_HREF} className="hidden items-center gap-2 text-sm text-white/80 transition-colors duration-300 hover:text-gold xl:flex">
            <Phone className="h-3.5 w-3.5 text-gold" />
            {PHONE}
          </a>
          <button
            data-testid="nav-reserve-button"
            onClick={() => go('#reserve')}
            className="bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-transform duration-300 hover:scale-[1.03] hover:bg-gold-bright"
          >
            Reserve
          </button>
        </div>

        <button data-testid="mobile-menu-toggle" className="text-white lg:hidden" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden border-t border-white/5 bg-ink lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-display text-2xl text-white/85 transition-colors duration-300 hover:text-gold"
                >
                  {l.label}
                </button>
              ))}
              <a data-testid="mobile-nav-phone" href={PHONE_HREF} className="mt-4 flex items-center gap-2 text-gold">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
